import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.js";
import { fetchPrivateCodeFromAPI, sendPrivateCode } from "../api/users.js";
import SEO from "../components/SEO.js";
import img from "../RapidMind 2025.png";
import "../index.css";

// Recharts imports
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

function Home() {
  const navigate = useNavigate();
  const { user, refreshUserData, setUser } = useAuth();
  const [usageData, setUsageData] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showPrivateCode, setShowPrivateCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [isLoadingPrivateCode, setIsLoadingPrivateCode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Plan colors
  const planColors = {
    Free: "#22c55e",       // Green
    Pro: "#3b82f6",        // Blue
    Semester: "#8b5cf6",   // Violet
    Enterprise: "#ec4899", // Pink
  };

  // Copy user ID to clipboard
  const copyUserId = async () => {
    const id = user?.id || 'N/A';
    if (id !== 'N/A') {
      try {
        await navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  // Load usage data on component mount
  useEffect(() => {
    const loadUsageData = async () => {
      if (user?.id) {
        try {
          const token = localStorage.getItem('token') || await user.getIdToken(); // Fallback to Firebase token if JWT not available
          const response = await fetch('/api/users/usage-stats', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUsageData(data.weekly || []);
          } else {
            console.warn('Usage stats fetch failed, using fallback');
          }
        } catch (error) {
          console.error('Error loading usage data:', error);
        }
      }
      setLoading(false);
    };

    loadUsageData();
  }, [user]);

  // Refresh user data when component mounts
  useEffect(() => {
    if (user) {
      console.log('Home - User found on mount, refreshing data:', user);
      console.log('Home - Current private code:', user?.privateCode);
      refreshUserData();
    } else {
      console.log('Home - No user found on mount');
    }
  }, [user, refreshUserData]);

  // Get private code with multiple fallback strategies
  const getPrivateCode = () => {
    console.log('Home - getPrivateCode called');
    console.log('Home - Current user object:', user);

    // Strategy 1: Check user context first
    if (user?.privateCode && user.privateCode !== 'No private code available') {
      console.log('Home - Found private code in user context:', user.privateCode);
      return user.privateCode;
    }

    // Strategy 2: Check localStorage for user data
    try {
      const storedUser = localStorage.getItem('user');
      console.log('Home - Raw localStorage user data:', storedUser);

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.log('Home - Parsed localStorage user data:', parsedUser);

        if (parsedUser?.privateCode && parsedUser.privateCode !== 'No private code available') {
          console.log('Home - Using private code from localStorage:', parsedUser.privateCode);
          return parsedUser.privateCode;
        }

        // Strategy 3: Check if privateCode exists in any nested object
        if (parsedUser?.profile?.privateCode) {
          console.log('Home - Found private code in profile:', parsedUser.profile.privateCode);
          return parsedUser.profile.privateCode;
        }

        // Strategy 4: Try to extract from registration message
        if (parsedUser?.message) {
          const match = parsedUser.message.match(/\b\d{6}\b/); // Look for exactly 6 digits
          if (match) {
            console.log('Home - Extracted private code from message:', match[0]);
            return match[0];
          }
        }

        // Strategy 5: Check for any numeric field that might be the private code
        const userKeys = Object.keys(parsedUser);
        for (const key of userKeys) {
          if (typeof parsedUser[key] === 'string' && /^\d{6}$/.test(parsedUser[key])) {
            console.log('Home - Found potential private code in field:', key, parsedUser[key]);
            return parsedUser[key];
          }
        }
      }
    } catch (error) {
      console.error('Home - Error parsing stored user data:', error);
    }

    // Strategy 6: If all else fails, try to generate a temporary code for display
    console.log('Home - No private code found, attempting to fetch from API...');
    // Don't return "No private code available" immediately, try API call
    return null; // Return null to trigger API fetch
  };

  // Debug effect to monitor user changes and clear loading when privateCode is updated
  useEffect(() => {
    console.log('Home - User context updated:', user);
    console.log('Home - Private code in updated user:', user?.privateCode);
    if (user?.privateCode) {
      setIsLoadingPrivateCode(false);
    }
  }, [user]);

  // If user is not loaded, show loading
  if (!user) {
    return (
      <div className="home-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading user data...</p>
      </div>
    );
  }

  console.log('Home - User data:', user);
  console.log('Home - Private code:', user?.privateCode);

  return (
    <>
      <SEO
        title="RapidMind AI Dashboard - Your AI Workspace"
        description="Access your personalized AI workspace. Generate images, create content, and manage your AI tools with RapidMind AI. Track usage, upgrade plans, and explore advanced features."
        keywords="AI dashboard, AI workspace, content creation, image generation, rapidmind ai, user dashboard, AI tools management"
        image="/RapidMind 2025.png"
        type="website"
      />
      <div className="home-page">
        <div className="blue-background-block">
          <header className="header" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'center',
            width: '100%'
          }}>
            <img src={img} alt="RapidMind Logo" style={{ maxWidth: '5%', height: 'auto', marginBottom: 0 }} />
            <h1 style={{ marginTop: '42px' }}>Bienvenue sur RapidMind</h1>
          </header>

          <div className="header T" style={{
            marginTop: '0px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}>
            <p>🌟 Explore, Create, and Share with Ease! 🌟 </p>
            <p>🎨 Generate Stunning Images – Turn your ideas into visual masterpieces.</p>
          </div>
        </div>

      {/* Welcome Section */}
      <main style={{ padding: "40px", color: "#eaeaea" }}>
        <h1 style={{ marginBottom: "10px" }}>
          👋 Welcome back, <span style={{ color: "#fff" }}>
            {user?.firstName ? user.firstName + (user.lastName ? ' ' + user.lastName : '') : user?.displayName || 'User'}
          </span>!
        </h1>
        <p style={{ marginBottom: "10px" }}>
          <code
            style={{ background: "#222", padding: "4px 8px", borderRadius: "6px", cursor: "pointer" }}
            onClick={copyUserId}
            title="Click to copy ID"
          >
            {copied ? 'Copied!' : (user?.id || 'N/A')}
          </code>
        </p>
        <p style={{ marginBottom: "30px" }}>
          Private Code: 🔒 <code
            style={{ background: "#222", padding: "4px 8px", borderRadius: "6px", cursor: "pointer", userSelect: "text" }}
            title="Click to copy Private Code"
          >
            {showPrivateCode ? (isLoadingPrivateCode ? '⏳ Loading...' : (getPrivateCode() || '⏳ Fetching...')) : '••••••'}
          </code>
          <button
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              marginLeft: "8px",
              fontSize: "14px"
            }}
            onClick={() => setShowPrivateCode(!showPrivateCode)}
            title={showPrivateCode ? 'Hide code' : 'Show code'}
          >
            {showPrivateCode ? '🙈' : '👁️'}
          </button>
          <button
            style={{
              background: "none",
              border: "none",
              color: "#3b82f6",
              cursor: "pointer",
              marginLeft: "4px",
              fontSize: "14px"
            }}
            onClick={async () => {
              const privateCode = getPrivateCode();
              if (navigator.clipboard && privateCode && privateCode !== '⏳ Fetching...') {
                await navigator.clipboard.writeText(privateCode);
                setCopiedCode(true);
                setTimeout(() => setCopiedCode(false), 2000);
              }
            }}
            title="Copy private code"
          >
            {copiedCode ? '✓' : '📋'}
          </button>
          <br />
          <span style={{ fontSize: "12px", color: "#ff6b6b", marginTop: "5px", display: "inline-block" }}>
            ⚠️ <strong>Important:</strong> Keep this code secure and private. Do not share it with anyone.
          </span>
        </p>

        {/* Plan + Stats Container */}
        <div className="stats-container" style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>

          {/* Plan Card */}
          <div className="plan-card">
            <h3 style={{ marginBottom: "8px" }}>Your Current Plan</h3>
            <p style={{
              fontWeight: "bold",
              fontSize: "20px",
              color: planColors[user?.planStatus] || "#ccc"
            }}>
              {user?.planStatus || 'Free'}
            </p>
            <button
              className="btn-primary"
              style={{ marginTop: "15px" }}
              onClick={() => navigate("/pricing")}
            >
              Upgrade Plan
            </button>
          </div>

          {/* Usage Statistics */}
          <div className="usage-card">
            <h3 style={{ marginBottom: "10px" }}>📊 Weekly Token Usage</h3>
            <div style={{ marginBottom: "15px", fontSize: "14px", color: "#ccc" }}>
              <p>Track your AI token consumption this week</p>
              <p style={{ fontSize: "12px", marginTop: "5px" }}>
                💡 <strong>Token Usage:</strong> Text generation (100 tokens), Image creation (500 tokens), Voice input (50 tokens)
              </p>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={usageData}>
                <XAxis dataKey="day" stroke="#aaa" fontSize={12} />
                <YAxis stroke="#aaa" fontSize={12} />
                <Tooltip
                  formatter={(value, name) => [`${value} tokens`, 'Usage']}
                  labelStyle={{ color: '#fff' }}
                  contentStyle={{
                    backgroundColor: '#333',
                    border: '1px solid #555',
                    borderRadius: '8px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="tokens"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#3b82f6' }}
                  activeDot={{ r: 6, fill: '#1e40af' }}
                />
              </LineChart>
            </ResponsiveContainer>
            <div style={{
              marginTop: "15px",
              padding: "10px",
              background: "#2a2a2a",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#ccc"
            }}>
              <p><strong>Plan Limits ({user?.planStatus || 'Free'} Plan):</strong></p>
              {user?.planStatus === 'Free' && <p>• 1,000 tokens/month (recharged on subscription)</p>}
              {user?.planStatus === 'Pro' && <p>• 10,000 tokens/month (auto-recharged after payment)</p>}
              {user?.planStatus === 'Enterprise' && <p>• Unlimited tokens (auto-recharged)</p>}
              <p style={{ fontSize: "11px", marginTop: "5px", color: "#94a3b8" }}>
                💡 Tokens recharge automatically on monthly renewal based on your plan choice.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          marginTop: "40px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px"
        }}>
          <div className="card" onClick={() => {
            // Get workspaceId from user data or localStorage
            let workspaceId = user?.workspaceId || localStorage.getItem('workspaceId');
            let userRole = user?.role || localStorage.getItem('userRole');

            if (workspaceId) {
              // Map workspaceId to direct workspace routes
              const workspaceRoutes = {
                1: '/FreelancerWorkSpace', // freelancer
                2: '/StudentWorkSpace',    // student
                3: '/CreatorWorkSpace',    // content_creator
                4: '/InfluencerWorkspace', // influencer
                5: '/EntrepreneurWorkSpace' // entrepreneur
              };

              const route = workspaceRoutes[parseInt(workspaceId)];
              if (route) {
                navigate(route);
              } else {
                navigate('/workspace');
              }
            } else if (userRole) {
              // Fallback to role-based routing
              const roleRoutes = {
                'freelancer': '/FreelancerWorkSpace',
                'student': '/StudentWorkSpace',
                'content_creator': '/CreatorWorkSpace',
                'influencer': '/InfluencerWorkspace',
                'entrepreneur': '/EntrepreneurWorkSpace'
              };

              const route = roleRoutes[userRole];
              if (route) {
                navigate(route);
                navigate('/workspace');
              }
            } else {
              navigate('/workspace');
            }
          }}>
            <h4>🚀 Go to Workspace</h4>
            <p>Start creating prompts, generating images & text instantly.</p>
          </div>
          <div className="card" onClick={() => navigate("/profile")}>
            <h4>👤 Manage Profile</h4>
            <p>Update your info, change password, or delete account.</p>
          </div>
          <div className="card" onClick={() => navigate("/BillingPlans")}>
            <h4>💳 Billing & Plans</h4>
            <p>Check your subscription details and upgrade options.</p>
          </div>
        </div>
      </main>
    </div>
    </>
  );
}


export default Home;